import { API_URL } from "../../constants/constants";
import { ResponseDataType, listDataType } from "../../types";
import { apiSlice } from "../api/apiSlice";

const apiWithTags = apiSlice.enhanceEndpoints({
    addTagTypes: ["credentialList", "myCredentials", "sleetProjectList", "sleetProject"],
});

/**
 * The `CredentialsSlice` is an API slice created using `apiWithTags.injectEndpoints`.
 * It defines an endpoint for fetching a list of credentials with various query parameters.
 *
 * @endpoint credentialList
 * @queryParam {string} search - A search term to filter the credentials.
 * @queryParam {number} limit - The maximum number of credentials to return.
 * @queryParam {number} current_page - The current page number for pagination.
 * @queryParam {string} sort_by - The field by which to sort the credentials.
 * @queryParam {string} sort_direction - The direction in which to sort the credentials (e.g., 'asc' or 'desc').
 *
 * @returns {ResponseDataType} The response data containing the list of credentials.
 *
 * @providesTags ["credentialList"]
 */
export const CredentialsSlice = apiWithTags.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Retrieves a list of credentials from the server.
         * @param search - A search term to filter the credentials.
         * @param limit - The maximum number of credentials to return.
         * @param current_page - The current page number for pagination.
         * @param sort_by - The field by which to sort the credentials.
         * @param sort_direction - The direction in which to sort the credentials.
         * @param project_id - The ID of the project to filter the credentials by.
         * @returns A promise that resolves to the list of credentials.
         * @tag credentialList
         */
        credentialList: builder.query<ResponseDataType, listDataType>({
            query: ({ search, limit, current_page, sort_by, sort_direction, project_id }) => ({
                url:
                    API_URL.SLEET +
                    API_URL.CREDENTIALS +
                    `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
                    }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
                    }${project_id ? `&project_id=${project_id}` : ""
                    }${search ? `&search=${encodeURIComponent(search)}` : ""}`,

                method: "GET",
            }),
            providesTags: ["credentialList"],
        }),


        /**
         * Retrieves the credentials of the current user.
         * @returns A promise that resolves to the current user's credentials.
         * @tag myCredentials
         */

        myCredentials: builder.query<ResponseDataType, void>({
            query: () => ({
                url: API_URL.SLEET + API_URL.CREDENTIALS + "/me",
                method: "GET",
            }),
            providesTags: ["myCredentials"],
        }),

        /**
         * Creates a new credential on the server.
         * @param formData - The form data containing the credential information.
         * @returns A promise that resolves to the response data for the created credential.
         * @invalidatesTags ["credentialList"]
         */
        CreateCredential: builder.mutation<ResponseDataType, FormData>({
            query: (formData) => ({
                url: API_URL.SLEET + API_URL.CREDENTIALS,
                method: "POST",
                body: formData,
            }),
            invalidatesTags: ["credentialList", "myCredentials"],
        }),

        /**
         * Updates an existing credential on the server.
         * @param id - The ID of the credential to update.
         * @param formData - The form data containing the updated credential information.
         * @returns A promise that resolves to the response data for the updated credential.
         * @invalidatesTags ["credentialList"]
         */
        UpdateCredential: builder.mutation<
            ResponseDataType,
            { id: string; formData: FormData }
        >({
            query: ({ id, formData }) => ({
                url: API_URL.SLEET + API_URL.CREDENTIALS + "/" + id,
                method: "PUT",
                body: formData,
            }),
            invalidatesTags: ["credentialList", "myCredentials"],
        }),

        /**
         * Deletes a credential from the server.
         * @param id - The ID of the credential to delete.
         * @returns A promise that resolves to the response data for the deleted credential.
         * @invalidatesTags ["credentialList"]
         */

        DeleteCredential: builder.mutation<ResponseDataType, string>({
            query: (id) => ({
                url: API_URL.SLEET + API_URL.CREDENTIALS + "/" + id,
                method: "DELETE",
            }),
            invalidatesTags: ["credentialList", "myCredentials"],
        }),

        /**
         * Changes the password of a credential.
         * @param formData - The form data containing the credential ID and new password.
         * @returns A promise that resolves to the response data.
         * @invalidatesTags ["credentialList"]
         */

        changeCredential: builder.mutation<ResponseDataType, FormData>({
            query: (formData) => ({
                url: API_URL.SLEET + API_URL.CHANGE_CREDENTIAL,
                method: "POST",
                body: formData,
            }),
            invalidatesTags: ["credentialList", "myCredentials"],
        }),

        /**
         * Sends a forgot credential request to the server.
         * @param formData - The form data containing the email address of the user.
         * @returns A promise that resolves to the response data.
         */
        forgotCredential: builder.mutation<ResponseDataType, FormData>({
            query: (formData) => ({
                url: API_URL.SLEET + API_URL.FORGOT_CREDENTIAL,
                method: "POST",
                body: formData,
            }),
        }),

        /**
         * Sends a reset credential request to the server.
         * @param formData - The form data containing the email address and new password.
         * @returns A promise that resolves to the response data.
         * @invalidatesTags ["credentialList"]
         */

        resetCredential: builder.mutation<ResponseDataType, FormData>({
            query: (formData) => ({
                url: API_URL.SLEET + API_URL.RESET_CREDENTIAL,
                method: "POST",
                body: formData,
            }),
            invalidatesTags: ["credentialList", "myCredentials"],
        }),

        sleetProjectList: builder.query<ResponseDataType, listDataType>({
            query: ({ search, limit, current_page, sort_by, sort_direction }) => ({
                url:
                    API_URL.SLEET +
                    API_URL.PROJECTS +
                    `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
                    }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
                    }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
                method: "GET",
            }),
            providesTags: ["sleetProjectList"],
        }),

        createSleetProject: builder.mutation<ResponseDataType, FormData>({
            query: (formData) => ({
                url: API_URL.SLEET + API_URL.PROJECTS,
                method: "POST",
                body: formData,
            }),
            invalidatesTags: ["sleetProjectList"],
        }),

        getSleetProject: builder.query<ResponseDataType, string>({
            query: (id) => ({
                url: API_URL.SLEET + API_URL.PROJECTS + "/" + id,
                method: "GET",
            }),
            providesTags: ["sleetProject"],
        }),

        getSleetProjectMembers: builder.query<ResponseDataType, listDataType>({
            query: ({ id, search, limit, current_page, sort_by, sort_direction }) => ({
                url:
                    API_URL.SLEET +
                    API_URL.PROJECTS +
                    "/" +
                    id +
                    API_URL.MEMBERS +
                    `?${limit ? `limit=${limit}` : ""}${current_page ? `&current_page=${current_page}` : ""
                    }${sort_by ? `&sort_by=${sort_by}` : ""}${sort_direction ? `&sort_direction=${sort_direction}` : ""
                    }${search ? `&search=${encodeURIComponent(search)}` : ""}`,
                method: "GET",
            }),
            providesTags: ["sleetProject"],
        }),

        updateSleetProject: builder.mutation<
            ResponseDataType,
            { id: string; formData: FormData }
        >({
            query: ({ id, formData }) => ({
                url: API_URL.SLEET + API_URL.PROJECTS + "/" + id,
                method: "POST",
                body: formData,
            }),
            invalidatesTags: ["sleetProjectList", "sleetProject"],
        }),

        deleteSleetProject: builder.mutation<ResponseDataType, string>({
            query: (id) => ({
                url: API_URL.SLEET + API_URL.PROJECTS + "/" + id,
                method: "DELETE",
            }),
            invalidatesTags: ["sleetProjectList", "sleetProject"],
        }),
    }),
});

export const {
    useCredentialListQuery,
    useMyCredentialsQuery,
    useCreateCredentialMutation,
    useUpdateCredentialMutation,
    useDeleteCredentialMutation,
    useChangeCredentialMutation,
    useForgotCredentialMutation,
    useResetCredentialMutation,
    useSleetProjectListQuery,
    useCreateSleetProjectMutation,
    useGetSleetProjectQuery,
    useUpdateSleetProjectMutation,
    useDeleteSleetProjectMutation,
    useGetSleetProjectMembersQuery,
} = CredentialsSlice;
